import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = 'May 13, 2026';

  const sections = [
    {
      title: 'Agreement',
      content: [
        {
          text: 'This Privacy Policy is an agreement between you ("you" or "your") and Z37 LLC ("we", "us", or "our"), a New York limited liability company. It describes how we collect, use, and protect information when you visit z37.io or use our services.'
        }
      ]
    },
    {
      title: 'Information We Collect',
      content: [
        {
          subtitle: 'Information You Provide',
          text: 'When you use our contact form or lead capture, we collect information you voluntarily provide, including your name, email address, company name, and the content of your message.'
        },
        {
          subtitle: 'Discovery Chat Conversations',
          text: 'Our discovery chat tool lets you describe product ideas, business plans, and technical needs in conversation with our AI assistant. The messages you type are sent directly to our servers and processed by a third-party AI service (Google Gemini) to generate responses. We do not sanitize or alter your messages — they are passed through as-is to provide the best conversational experience. If you prefer to discuss a project without AI processing, contact us directly at hello@z37.io.'
        },
        {
          subtitle: 'AI-Generated Wireframes',
          text: 'When you use the wireframe generation feature, your conversation history is sent to Google Gemini to produce visual wireframes. Before transmission to the image generation model, we automatically strip email addresses, phone numbers, Social Security numbers, and credit card numbers from the content. Wireframe images are returned directly to your browser and are not stored on our servers.'
        },
        {
          subtitle: 'Automatically Collected Information',
          text: 'We collect standard usage data such as browser type, device information, pages visited, and time spent on site. This helps us improve our services.'
        }
      ]
    },
    {
      title: 'How We Use Your Information',
      content: [
        {
          text: 'We use the information we collect to:'
        },
        {
          list: [
            'Respond to your inquiries and provide requested services',
            'Power the AI discovery chat and wireframe generation features',
            'Improve our website, AI models, and user experience',
            'Send relevant updates about our services (with your consent)',
            'Comply with legal obligations'
          ]
        }
      ]
    },
    {
      title: 'Third-Party Services',
      content: [
        {
          text: 'We use the following third-party services that may process your data:'
        },
        {
          subtitle: 'Google Gemini AI (google.com)',
          text: 'Your discovery chat messages and wireframe generation requests are sent to Google\'s Gemini API. We sanitize personally identifiable information before transmission where feasible, but the substance of your conversation (product ideas, business context) is processed by Google. Google\'s own privacy policy governs their handling of this data. We do not send your data to any other AI providers.'
        },
        {
          subtitle: 'Amazon Web Services (aws.amazon.com)',
          text: 'Our application runs on AWS infrastructure. Lambda functions process your requests, DynamoDB may store rate-limiting data, and SSM stores our API credentials. AWS does not access or use your data for their own purposes.'
        }
      ]
    },
    {
      title: 'Confidentiality',
      content: [
        {
          text: 'We understand that you may share sensitive business information, trade secrets, or proprietary ideas through our discovery chat. While we take reasonable measures to protect your data (including PII sanitization for wireframe generation and server-side encryption), we want to be transparent:'
        },
        {
          list: [
            'Your chat messages are processed by Google Gemini — we cannot guarantee Google\'s treatment of this data beyond their published API terms',
            'Wireframe images are generated by AI and returned to your browser — we do not store them on our servers',
            'Chat history is stored only in your browser\'s local session storage and is not persisted in our database',
            'We do not use your conversations to train AI models or share them with other parties',
            'If you prefer to discuss a project without AI processing, contact us directly at hello@z37.io'
          ]
        }
      ]
    },
    {
      title: 'Data Retention',
      content: [
        {
          text: 'Discovery chat conversations are stored only in your browser\'s local session storage. Clearing your browser data or using the "New Chat" button removes them entirely. We do not currently persist chat history or wireframe images in our own databases. Server-side request logs are retained temporarily for operational purposes and debugging, and are purged on a rolling basis.'
        }
      ]
    },
    {
      title: 'AI-Generated Content',
      content: [
        {
          text: 'Wireframes and chat responses produced by our AI features are generated by machine learning models and may contain inaccuracies. AI-generated content is provided for exploratory and illustrative purposes only. You retain ownership of the ideas and information you share. We make no warranty as to the accuracy, completeness, or fitness of AI-generated outputs for any particular purpose.'
        }
      ]
    },
    {
      title: 'Cookies and Tracking',
      content: [
        {
          text: 'We use essential cookies to ensure our website functions properly. We may use analytics cookies to understand how visitors interact with our site. You can control cookie preferences through your browser settings.'
        }
      ]
    },
    {
      title: 'Data Sharing',
      content: [
        {
          text: 'We do not sell, trade, or rent your personal information to third parties. We share data only with the service providers described above (Google Gemini, AWS) to the extent necessary to operate our website and AI features. These parties are contractually obligated to keep your information confidential.'
        }
      ]
    },
    {
      title: 'Data Security',
      content: [
        {
          text: 'We implement appropriate technical and organizational measures to protect your information, including:'
        },
        {
          list: [
            'Automated sanitization of email addresses, phone numbers, SSNs, and credit card numbers before wireframe image generation',
            'Server-side encryption for data in transit and at rest',
            'API credentials stored in AWS SSM Parameter Store with encryption',
            'Rate limiting to prevent abuse'
          ]
        },
        {
          text: 'No method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.'
        }
      ]
    },
    {
      title: 'Your Rights',
      content: [
        {
          subtitle: 'GDPR Rights (EU Residents)',
          text: 'If you are a resident of the European Union, you have the right to access, rectify, erase, restrict processing, data portability, and object to processing of your personal data.'
        },
        {
          subtitle: 'CCPA Rights (California Residents)',
          text: 'California residents have the right to know what personal information is collected, request deletion of personal information, and opt out of the sale of personal information (which we do not engage in).'
        },
        {
          subtitle: 'New York Residents',
          text: 'Under New York law, you have the right to be informed about how your personal information is collected and used, and to request access to or deletion of your data.'
        }
      ]
    },
    {
      title: 'Changes to This Policy',
      content: [
        {
          text: 'We may update this Privacy Policy from time to time. We will update the "Last updated" date at the top of this page. Your continued use of our website after any changes indicates your acceptance of the revised policy.'
        }
      ]
    },
    {
      title: 'Contact Us',
      content: [
        {
          text: 'If you have questions about this Privacy Policy, wish to exercise your data rights, or prefer to discuss a project without AI processing, please contact us at:'
        },
        {
          contact: {
            email: 'hello@z37.io',
            address: 'Z37 LLC, New York, USA'
          }
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `radial-gradient(circle at 30% 30%, rgba(0, 229, 255, 0.15) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-accent transition-colors mb-8"
            >
              &larr; Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">
              Privacy Policy
            </h1>
            <p className="text-secondary text-lg">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-invert prose-lg"
            >
              <p className="text-secondary leading-relaxed mb-12">
                At Z37, we are committed to protecting your privacy. This Privacy Policy explains 
                how we collect, use, and safeguard your information when you visit our website 
                or use our services, including our AI-powered discovery chat and wireframe tools.
              </p>

              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
                  className="mb-12"
                >
                  <h2 className="text-2xl font-display font-semibold text-primary mb-4">
                    {section.title}
                  </h2>
                  
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="mb-4">
                      {item.subtitle && (
                        <h3 className="text-lg font-medium text-accent mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      {item.text && (
                        <p className="text-secondary leading-relaxed">
                          {item.text}
                        </p>
                      )}
                      {item.list && (
                        <ul className="list-disc list-inside text-secondary space-y-2 mt-2">
                          {item.list.map((listItem, listIndex) => (
                            <li key={listIndex}>{listItem}</li>
                          ))}
                        </ul>
                      )}
                      {item.contact && (
                        <div className="bg-white/[0.02] border border-borders rounded-lg p-6 mt-4">
                          <p className="text-secondary">
                            <strong className="text-primary">Email:</strong>{' '}
                            <a 
                              href={`mailto:${item.contact.email}`}
                              className="text-accent hover:text-signal-lime transition-colors"
                            >
                              {item.contact.email}
                            </a>
                          </p>
                          <p className="text-secondary mt-2">
                            <strong className="text-primary">Address:</strong> {item.contact.address}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
